// Top-up plan BUILDER + customer tap-to-pay entry points (tv-1a9, Feature B).
// Two mutating flows sit either side of tv-opw's reconciler and tv-qta's chase:
//
//   createTopupPlan     the agent builds the pay-over-time plan inside the
//                       deposit-send modal (a start date, a cadence, a
//                       per-instalment amount). The first instalment (>= $50)
//                       holds the date; the rest come via tap-to-pay.
//   payTopupInstalment  the CUSTOMER pays the next instalment from the hosted
//                       tap-to-pay page. Distinct from tv-qta's topupChasePays,
//                       which is the agent CHASE-context pay.
//
// Follows the *Flow.ts convention: read the store, mutate via the brain-store
// upserts, appendActivity, emit on the eventBus. "Now" is the sim clock, never
// Date.now(); money is whole AUD dollars. Reuses tv-w98's pure helpers.

import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { eventBus } from '@/events/eventBus';
import { generateInstalments, isTopupPlanComplete } from '@/domain/topupPlan';
import { depositPaidAud } from '@/utils/bookingSummary';
import type { DepositLedgerEntry, TopupFrequency, TopupInstalment, TopupPlan } from '@/domain/types';

function nowIso(): string {
  return useSimulationStore.getState().clockIso;
}

/** The builder's floor: an instalment below this is refused. */
export const MIN_INSTALMENT_AUD = 50;

/**
 * Build + persist a pay-over-time plan for a deposit. `totalAud` defaults to the
 * deposit's `amountAud` (the full deposit; the first instalment >= $50 holds the
 * date). Mints `topup-<depositId>`, derives customerId / bookingId
 * (`deposit.bookingId ?? deposit.heldBookingId`) / studioId from the deposit, and
 * lays the instalment schedule via `generateInstalments`. Guard: the deposit must
 * exist, `totalAud` must be positive and `instalmentAud` at least $50.
 */
export function createTopupPlan(opts: {
  depositId: string;
  startIso: string;
  frequency: TopupFrequency;
  instalmentAud: number;
  totalAud?: number;
  by?: string;
  at?: string;
}): { ok: boolean; planId?: string } {
  const brain = useBrainStore.getState();
  const deposit = brain.deposits[opts.depositId];
  if (!deposit) return { ok: false };

  const totalAud = opts.totalAud ?? deposit.amountAud;
  if (totalAud <= 0) return { ok: false };
  if (opts.instalmentAud < MIN_INSTALMENT_AUD) return { ok: false };

  const at = opts.at ?? nowIso();
  const planId = `topup-${deposit.id}`;
  const bookingId = deposit.bookingId ?? deposit.heldBookingId;
  const instalments = generateInstalments({
    startIso: opts.startIso,
    frequency: opts.frequency,
    instalmentAud: opts.instalmentAud,
    totalAud,
  });

  const plan: TopupPlan = {
    id: planId,
    depositId: deposit.id,
    customerId: deposit.customerId,
    studioId: deposit.studioId,
    totalAud,
    frequency: opts.frequency,
    startIso: opts.startIso,
    instalments,
    status: 'active',
    createdAt: at,
    ...(bookingId ? { bookingId } : {}),
    ...(opts.by ? { createdByAgentId: opts.by } : {}),
  };
  brain.upsertTopupPlan(plan);

  // Reuse an existing activity type (deposit-link-sent): a payment arrangement
  // for a deposit went out. No new ActivityFeedEntry union member.
  brain.appendActivity({
    id: `act-topup-plan-${planId}-${at}`,
    type: 'deposit-link-sent',
    customerId: deposit.customerId,
    studioId: deposit.studioId,
    artistId: deposit.artistId,
    amount: totalAud,
    body: `Pay-over-time plan set up: ${instalments.length} ${opts.frequency} instalments totalling $${totalAud}.`,
    timestamp: at,
  });
  eventBus.publish('topup:plan-created', {
    planId,
    depositId: deposit.id,
    customerId: deposit.customerId,
    totalAud,
    instalments: instalments.length,
    at,
  });
  return { ok: true, planId };
}

/**
 * Pay the earliest unpaid instalment (the customer tap-to-pay entry point). Marks
 * the earliest `scheduled` / `reminded` / `missed` instalment `paid` (paidAt=at),
 * appends a `topup` ledger movement to the deposit so its paid balance grows by
 * the instalment amount (whole dollars), and flips the plan to `completed` once
 * every instalment is settled. Publishes `topup:instalment-paid`. Guards: unknown
 * plan, or a plan that is no longer active, return `{ ok: false }`.
 */
export function payTopupInstalment(
  planId: string,
  opts?: { by?: string; at?: string },
): { ok: boolean; amountAud?: number } {
  const brain = useBrainStore.getState();
  const plan = brain.topupPlans[planId];
  if (!plan) return { ok: false };
  if (plan.status !== 'active') return { ok: false };

  const at = opts?.at ?? nowIso();
  const by = opts?.by ?? 'customer';

  const idx = plan.instalments.findIndex(
    (i) => i.status === 'scheduled' || i.status === 'reminded' || i.status === 'missed',
  );
  if (idx === -1) return { ok: false };

  const instalment = plan.instalments[idx]!;
  const instalments: TopupInstalment[] = plan.instalments.map((inst, i) =>
    i === idx ? { ...inst, status: 'paid', paidAt: at } : inst,
  );
  const nextPlan: TopupPlan = { ...plan, instalments };
  const status: TopupPlan['status'] = isTopupPlanComplete(nextPlan) ? 'completed' : plan.status;
  brain.upsertTopupPlan({ ...nextPlan, status });

  // Grow the deposit's paid balance by this instalment. Preserve the prior paid
  // history (backfill a `paid` entry when the deposit has no ledger yet) so the
  // ledger-derived balance rises rather than replacing what was already in.
  const deposit = brain.deposits[plan.depositId];
  if (deposit) {
    const priorPaid = depositPaidAud(deposit);
    const baseLedger: DepositLedgerEntry[] =
      deposit.ledger && deposit.ledger.length > 0
        ? deposit.ledger
        : priorPaid > 0
          ? [{ at: deposit.paidAt ?? at, amountAud: priorPaid, kind: 'paid', by: deposit.takenByAgentId ?? 'system' }]
          : [];
    brain.upsertDeposit({
      ...deposit,
      amountPaid: priorPaid + instalment.amountAud,
      ledger: [
        ...baseLedger,
        { at, amountAud: instalment.amountAud, kind: 'topup', by, note: 'Top-up instalment paid' },
      ],
    });
  }

  brain.appendActivity({
    id: `act-topup-instalment-${plan.id}-${at}`,
    type: 'deposit-paid',
    customerId: plan.customerId,
    studioId: plan.studioId,
    amount: instalment.amountAud,
    body:
      status === 'completed'
        ? `Final instalment paid: $${instalment.amountAud}. Plan paid in full.`
        : `Instalment paid: $${instalment.amountAud}.`,
    timestamp: at,
  });
  eventBus.publish('topup:instalment-paid', {
    planId: plan.id,
    depositId: plan.depositId,
    amountAud: instalment.amountAud,
    completed: status === 'completed',
    at,
  });
  return { ok: true, amountAud: instalment.amountAud };
}
